import { createApi, fetchBaseQuery, skipToken, } from "@reduxjs/toolkit/query/react";
import { AllForecastComponents, ForecastTime, } from "../app/forecastSurface";
import { createBaseQueryWithReauth, getAccessToken } from "../app/auth";
import { WeatherRiskConfig } from "../generated/gpb/api/risk";
import { SurfaceEnum } from "../components/UserVisibleStrings";
import { ForecastData, ForecastDateTime } from "../generated/gpb/api/route";

export interface ForecastSurfacePage
{
    start: number;
    limit: number;
}
export interface RequestForecastPayload
{
    surfaceName: SurfaceEnum;
    surface : ForecastSurfacePage;
    time: ForecastTime;
}

export interface ForecastImpactData
{
    tau: number;
    urls: Array<string>;
}

export interface ForecastWindData
{
    tau: number;
    url: string;
}

export interface PagedResponse
{
    _metadata: {
        size: number,
        start: number,
        limit: number,
        total: number,
    };
    data: Array<ForecastImpactData>;
}

export interface ImpactSurfaceResponse
{
    index: number;
    impactUrls: ForecastImpactData;
}

export const riskSurfaceApi = createApi( {
    reducerPath: "riskSurfaceApi",
    baseQuery: createBaseQueryWithReauth(
        fetchBaseQuery( {
            baseUrl: process.env.REACT_APP_API_BASE + "/risksurface",
            credentials: "same-origin",
            prepareHeaders: ( headers ) =>
            {
                headers.set( "Accept", "application/json" );

                const accessToken = getAccessToken();
                if ( accessToken !== null )
                {
                    headers.set( "Authorization", `Bearer ${accessToken}` );
                }

                return headers;
            },
        } )
    ),
    endpoints: ( builder ) => ( {
        getAvailableSurfacesForTime: builder.query<
        AllForecastComponents,
        ForecastTime
        >( {
            query: ( time ) => `/${time.date}/${time.hour}`,
        } ),
        getRiskSurface: builder.query<Array<ImpactSurfaceResponse>, RequestForecastPayload>( {
            query: ( payload ) =>
            {
                const adjustedSurface = payload.surfaceName;
                return `/${payload.time.date}/${payload.time.hour}/${adjustedSurface}?start=${payload.surface.start}&limit=${payload.surface.limit}`;
            },
            transformResponse: ( response: PagedResponse ): Array<ImpactSurfaceResponse> =>
            {
                const impactAll: Array<ImpactSurfaceResponse> = [];
                let count = response._metadata.start;
                response.data.forEach( ( fc ) =>
                {
                    impactAll.push( { impactUrls: fc, index: count } );
                    count++;
                } );
                return impactAll;
            },
            serializeQueryArgs: ( {
                queryArgs,
            }: {
                queryArgs: RequestForecastPayload | typeof skipToken,
            } ) =>
            {
                return skipToken !== queryArgs
                    ? queryArgs.time.date +
              queryArgs.time.hour +
              queryArgs.surfaceName
                    : "INVALID";
            },
            forceRefetch( { currentArg, previousArg } )
            {
                return currentArg !== previousArg;
            },
        } ),
        addHistoricalForecast: builder.mutation<string, ForecastDateTime>( {
            query: ( payload ) => ( {
                url: `/historical?year=${payload.year}&month=${payload.month}&day=${payload.day}&hour=${payload.hour}`,
                method: "POST",
            } ),
        } ),
        deleteHistoricalForecast: builder.mutation<string, ForecastDateTime>( {
            query: ( payload ) =>
            {
                return `/historical?year=${payload.year}&month=${payload.month}&day=${payload.day}&hour=${payload.hour}&delete=true`;
            }
        } ),
        getHistoricalForecasts: builder.query<ForecastData, void>( {
            query: ( ) => ( {
                url: "/historical",
                method: "GET",
            } ),
        } ),
        getVersion: builder.query<string, void>( {
            query: () => ( {
                url: "/version",
                method: "GET",
            } ),
            transformResponse: ( data: { Version: string } ) => data.Version,
        } ),
        getLatestForecast: builder.query<ForecastTime, void>( {
            query: () => "/current",
        } ),
        getRiskConfig: builder.query<WeatherRiskConfig, void>( {
            query: ( ) => ( {
                url: "/risklevelconfig",
                method: "GET",
            } ),
            transformResponse: WeatherRiskConfig.fromJSON,
        } ),
        getWindDirectionData: builder.query<any, ForecastDateTime>( {
            query: ( payload ) => ( {
                url: `/winddirection?year=${payload.year}&month=${payload.month}&day=${payload.day}&hour=${payload.hour}`,
                method: "GET",
            } ),
        } ),
    } ),
} );

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetAvailableSurfacesForTimeQuery,
    useGetRiskSurfaceQuery,
    useGetLatestForecastQuery,
    useGetRiskConfigQuery,
    useGetVersionQuery,
    useLazyGetRiskSurfaceQuery,
    useAddHistoricalForecastMutation,
    useGetHistoricalForecastsQuery,
    useDeleteHistoricalForecastMutation,
    useGetWindDirectionDataQuery
} = riskSurfaceApi;
